import { postExchangeOffer } from "../../../app/api/exchangeOffer";
import { getBooking } from "../../../app/api/getBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { preferTrainTrips } from "../../../Lib/Utils/booking";
import { determinePassengerType, getPassengerAge, isActionRunSuccessfully, shouldSharePlaceWithGuardian } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { AdmissionStatusEnum, ApiResponseStatusEnum, BookingExchangeErrorCode, TrainTypeEnum, TripDirectionTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookedOffer, IBookingResponse, IBookingTypeTrip, IDeterminePassenger, IExchangeOfferRequest, IExchangeOfferResponse, INextAndPreviousTrip, IOffer, IPassenger, IQuickExchangeOfferRequest, IQuickExchangePassenger } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IGetAllTripWithoutExchangeActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/FilterTripAction";
import { IGetExchangeOfferByBookingProps } from "../../../Orchestrator/Types/Interface/ActionParams/SearchBookingParams";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";
import { getPreviousAndNextTrainAction } from "../QuickExchange/GetPreviousAndNextTrainAction";
import { bookedProductsForFulfillments } from "../../../Lib/Utils/exchangeProductTags";

class GetExchangeOfferByBooking extends BaseAction<IDefaultActionResult, IGetExchangeOfferByBookingProps> {

    override async run(): Promise<IDefaultActionResult> {
        let actionResult: IDefaultActionResult = {
            status: "fail",
            data: null,
            message: ""
        }
        try {
            const { nextBooking, fromUpcoming, cachedBookingResponse, probeOnly } = this.params!
            //Set Loader to false when it is from Upcoming
            const showLoader = fromUpcoming ? false : true;
            // Reuse the booking response if the caller already fetched it; this
            // avoids a redundant GET /retailer/bookings/{id} on the upcoming-list
            // pre-warm path, which previously fired the same request 4× per
            // round-trip booking.
            const responseBooking: any = cachedBookingResponse
                ? { status: 'ok', answer: cachedBookingResponse }
                : await getBooking(nextBooking.bookingId, showLoader);
            if (responseBooking.status === 'ok' && responseBooking.answer) {
                const bookingResponse: IBookingResponse = responseBooking.answer;
                const { booking } = bookingResponse

                const passenger = booking?.passengers;

                await getAllOfferWithoutExchangeAction.setParams(booking.bookedOffers)
                await getAllOfferWithoutExchangeAction.execute()
                let allBookedOfferWithoutExchange: IBookedOffer[] = getAllOfferWithoutExchangeAction.result!

                allBookedOfferWithoutExchange = allBookedOfferWithoutExchange.filter(offer => {
                    const fulfilledAdmissions = offer.admissions.filter(d => d.status !== AdmissionStatusEnum.REFUNDED)
                    return fulfilledAdmissions.length > 0
                })

                const params: IGetAllTripWithoutExchangeActionParams = {
                    bookedOffers: allBookedOfferWithoutExchange,
                    trips: booking.trips
                }
                await getAllTripWithoutExchangeAction.setParams(params)
                await getAllTripWithoutExchangeAction.execute()
                const allTripWithoutExchange: IBookingTypeTrip[] = getAllTripWithoutExchangeAction.result!

                if (!allTripWithoutExchange || allTripWithoutExchange.length === 0) {
                    return actionResult;
                }

                // If leg length is 1 then no need to do quick exchange
                // If leg length is more than 1 it means it's a connecting train
                // No quick exchange for connecting train
                let requiredToDoQuickExchange: boolean = true;
                requiredToDoQuickExchange = !(allTripWithoutExchange[0]?.legs?.length > 1)
                //[0] need to confirm                
                if (requiredToDoQuickExchange) {
                    //Get all Fulfilled passenger means without cancel passenger
                    const getAllFulfilledPassenger: IPassenger[] = []
                    const fulfilledPassengerIds = new Set<string>(); // Use a Set to track unique passenger IDs
                    passenger.forEach(pass => {
                        allBookedOfferWithoutExchange.forEach(offer => {
                            const hasFulfilledAdmissionForPassenger = offer.admissions.some(
                                add => add.status === AdmissionStatusEnum.FULFILLED && add.passengerIds.includes(pass.id)
                            );

                            if (hasFulfilledAdmissionForPassenger && !fulfilledPassengerIds.has(pass.id)) {
                                const determinePassenger: IDeterminePassenger = {
                                    age: pass?.age,
                                    cards: pass?.cards || [],
                                    prmNeeds: ((pass?.prmNeeds || []).length > 0 && pass?.prmNeeds) ? pass?.prmNeeds[0] : ""
                                }
                                const pType = determinePassengerType(determinePassenger)
                                if (pass.age !== 16 && pType === "adults") {
                                    // pass.age = 16
                                    // delete pass.dateOfBirth
                                }
                                getAllFulfilledPassenger.push(pass);
                                fulfilledPassengerIds.add(pass.id); // Add the passenger ID to the Set
                            }
                        })
                    })

                    let inboundTrip = preferTrainTrips(allTripWithoutExchange).find(trip => trip.direction === TripTypeEnum.IN_BOUND)
                    let outboundTrip: IBookingTypeTrip | undefined = undefined;

                    let bookedOfferOnly: IBookedOffer[] = []
                    // Filled by the per-trip probe loop below for the trip that
                    // matches `nextBookingTrip` (the next-ticket card). Used to
                    // drive the prev/next-train chip update that has always been
                    // single-trip by design.
                    let nextTicketProbeResponse: any = undefined;
                    let nextTicketBookedOffers: IBookedOffer[] | undefined = undefined;
                    let nextTicketFulfillmentIds: string[] = [];

                    const tripIds = nextBooking.tripIds || [];
                    const quickExchangeDirection = nextBooking.quickExchangedirection as TripTypeEnum | undefined;

                    // The "next-ticket" trip drives the prev/next-train chip update
                    // and the action's return value (only one ticket-card is shown
                    // at the top of the bookings page, so the chip update is
                    // single-trip by design). Pick it the same way as before:
                    // prefer a trip matching `quickExchangedirection`, then
                    // fall back to the first tripId.
                    let targetTripId: string | undefined;
                    if (Array.isArray(tripIds) && tripIds.length > 0) {
                        if (quickExchangeDirection) {
                            const candidateTrip = allTripWithoutExchange.find(
                                (trip) =>
                                    trip.direction === quickExchangeDirection &&
                                    tripIds.includes(trip.id)
                            );
                            targetTripId = candidateTrip?.id || tripIds[0];
                        } else {
                            targetTripId = tripIds[0];
                        }
                    }

                    // Probe EVERY trip in the booking, not just the next-ticket
                    // one — without this the modification atom holds only the
                    // first leg's verdict, and BookingData.tsx's per-trip gate
                    // silently falls through on multi-leg bookings (the exact
                    // bug this PR fix was reported as missing). Mirrors the
                    // per-trip probe loop in BookingOptions.tsx.
                    //
                    // Defensive fallback: when `nextBooking.tripIds` is empty
                    // (legacy data), use `allTripWithoutExchange` — which is
                    // already filtered to non-refunded trips upstream
                    // (`GetAllTripWithoutExchangeAction` only includes trips
                    // covered by non-REFUNDED admissions), so this can't
                    // accidentally probe a cancelled leg.
                    const probeTripIds: string[] = (Array.isArray(tripIds) && tripIds.length > 0)
                        ? tripIds
                        : allTripWithoutExchange.map((t) => t.id).filter(Boolean);
                    const tripsToProbe = probeTripIds
                        .map((id) => allTripWithoutExchange.find((t) => t.id === id))
                        .filter((t): t is IBookingTypeTrip => !!t);

                    // `nextBookingTrip` keeps its original meaning — the trip
                    // used to drive the prev/next-train chip update and the
                    // action's return value.
                    const nextBookingTrip = targetTripId
                        ? allTripWithoutExchange.find(trip => trip.id === targetTripId)
                        : tripsToProbe[0];

                    // Build per-trip payloads up-front so the async work below
                    // is a single Promise.all rather than sequential awaits.
                    // For a 2-leg round-trip this halves wall-clock latency on
                    // the background pre-warm (~4.8s → ~2.4s per the comment
                    // below). Legs that have no FULFILLED admissions for the
                    // current passenger set are filtered out here — there's
                    // nothing to exchange and the backend would 400 an empty
                    // payload. A skipped leg leaves the atom entry absent,
                    // which the gate treats as "unprobed → menu visible"
                    // (matches the details-page probe in BookingOptions.tsx).
                    const probeTasks = tripsToProbe.flatMap((probeTrip) => {
                        const offersForProbeTrip = allBookedOfferWithoutExchange.filter(
                            offer => offer?.tripCoverage?.coveredTripId === probeTrip.id
                        );
                        if (offersForProbeTrip.length === 0) return [];

                        const probeAdmissions = offersForProbeTrip.flatMap((offer) =>
                            offer?.admissions?.filter((admission) => {
                                const isFulfilled = admission.status === AdmissionStatusEnum.FULFILLED;
                                const matchesPassenger = admission.passengerIds.some((pid) =>
                                    getAllFulfilledPassenger.some((p) => p.id === pid)
                                );
                                return isFulfilled && matchesPassenger;
                            }) || []
                        );
                        const probeFulfillmentIds = probeAdmissions
                            .map((a) => a.id)
                            .filter(Boolean);
                        if (probeFulfillmentIds.length === 0) return [];

                        const probePayload: IQuickExchangeOfferRequest = {
                            fulfillmentIds: probeFulfillmentIds,
                            overruleCode: "String",
                            tripSearchCriteria: {
                                departureTime: probeTrip.startTime || "",
                                origin: { ...probeTrip.origin },
                                destination: { ...probeTrip.destination },
                                returnSearchParameters: null,
                            },
                            offerSearchCriteria: {
                                currency: booking?.confirmedPrice?.currency || booking?.provisionalPrice?.currency || "AED"
                            },
                            anonymousPassengerSpecifications: [...getAllFulfilledPassenger].map((passenger: IPassenger) => ({
                                externalRef: passenger.externalRef,
                                type: passenger.type,
                                cards: passenger?.cards || [],
                                prmNeeds: passenger.prmNeeds || [],
                                age: passenger.age,
                                sharePlaceWithGuardian: shouldSharePlaceWithGuardian(passenger) ? true : undefined
                            }))
                        };
                        return [{ probeTrip, offersForProbeTrip, probePayload }];
                    });

                    // Suppress error toast AND loader for the upcoming-list
                    // background prefetch — each call returns ~2 MB / ~2.4 s
                    // and only updates the next-trip "previous/next train"
                    // chips when it lands. Showing the global loader for this
                    // would block the page UX. When triggered explicitly (not
                    // from upcoming), keep the loader on.
                    const probeResults = await Promise.all(
                        probeTasks.map(async ({ probeTrip, offersForProbeTrip, probePayload }) => {
                            const probeResponse: any = await postExchangeOffer(
                                probePayload,
                                nextBooking.bookingId,
                                false,
                                showLoader
                            );
                            return { probeTrip, offersForProbeTrip, probeResponse, fulfillmentIds: probePayload.fulfillmentIds };
                        })
                    );

                    // Single atom write at the end with every leg's verdict
                    // merged in — N parallel probes triggered N atom writes
                    // previously, which fanned out N renders to every
                    // subscriber on the bookings-list page. One write keeps
                    // the page render count flat regardless of leg count.
                    //
                    // Only persist DEFINITIVE verdicts: ok → modifiable (false),
                    // or BOOKING_MODIFICATION_NOT_ALLOWED → blocked (true).
                    // Transient failures (5xx, validation, unrelated business
                    // errors) leave the entry absent so a future probe can
                    // retry — caching `false` on a 500 would persist
                    // "modifiable" for the whole session.
                    const verdicts: Record<string, boolean> = {};
                    for (const { probeTrip, offersForProbeTrip, probeResponse, fulfillmentIds } of probeResults) {
                        if (probeResponse?.status === ApiResponseStatusEnum.OK) {
                            verdicts[probeTrip.id] = false;
                        } else if (probeResponse?.errorCode === BookingExchangeErrorCode.BOOKING_MODIFICATION_NOT_ALLOWED) {
                            verdicts[probeTrip.id] = true;
                        }
                        // Stash the next-ticket trip's response so the
                        // single-trip chip update below uses it.
                        if (probeTrip.id === nextBookingTrip?.id) {
                            nextTicketProbeResponse = probeResponse;
                            nextTicketBookedOffers = offersForProbeTrip;
                            nextTicketFulfillmentIds = fulfillmentIds;
                            if (probeTrip.direction === TripTypeEnum.IN_BOUND) {
                                inboundTrip = undefined;
                                outboundTrip = preferTrainTrips(allTripWithoutExchange).find(trip => trip.direction === TripTypeEnum.OUT_BOUND);
                            }
                        }
                    }
                    if (Object.keys(verdicts).length > 0) {
                        const current =
                            (getRecoilStateValue('modificationNotAllowedBookingIdsAtom') as Record<string, Record<string, boolean>>) || {};
                        const bookingEntry = current[nextBooking.bookingId] || {};
                        const mergedEntry = { ...bookingEntry, ...verdicts };
                        // Only write when at least one verdict differs from
                        // the existing entry — otherwise no-op so subscribers
                        // don't render on identical state.
                        const changed = Object.keys(verdicts).some(
                            (tripId) => bookingEntry[tripId] !== verdicts[tripId],
                        );
                        if (changed) {
                            updateRecoilState('modificationNotAllowedBookingIdsAtom', {
                                ...current,
                                [nextBooking.bookingId]: mergedEntry,
                            });
                        }
                    }

                    // Pull the next-ticket response out of the loop so the
                    // existing prev/next-train chip update logic stays single-trip.
                    const exchangeOfferResponse: any = nextTicketProbeResponse;
                    if (nextTicketBookedOffers) {
                        bookedOfferOnly = nextTicketBookedOffers;
                    }

                    // Probe mode: only the modification-allowed signal is needed.
                    // Skip the next/previous-train chip update which would clobber
                    // nextAndPreviousTrainAtom (only meaningful for the next ticket).
                    if (
                        nextBookingTrip
                        && bookedOfferOnly.length > 0
                        && !probeOnly
                        && exchangeOfferResponse?.status === ApiResponseStatusEnum.OK
                        && exchangeOfferResponse?.answer
                    ) {
                        const bookingResponse: IExchangeOfferResponse = exchangeOfferResponse.answer;
                        if (bookingResponse?.trips.length > 0) {
                            const allTrips: INextAndPreviousTrip[] = bookingResponse.trips
                            // The booked admission products behind the tickets probed — not
                            // bookedOfferOnly[0], which can be a seat reservation. No guessing
                            // from the summary label (`selectedPriceClass` collapses Saver and
                            // Value, which mapped Value users to SFC): with exact-product quick
                            // exchange a wrong code is a wrong product, so no code → no chip.
                            const bookedProducts = bookedProductsForFulfillments(bookedOfferOnly, nextTicketFulfillmentIds);
                            const allBookedOffer: IOffer[] = bookingResponse.exchangeOffers
                            // Passengers on different products: one exact code would match
                            // (and price) only some of them, so offer no chip at all.
                            const isMixedFareLeg = new Set(bookedProducts.map((product) => product.code)).size > 1;
                            const nextBookingTicketTypeCode: string = isMixedFareLeg ? "" : bookedProducts[0]?.code || "";
                            getPreviousAndNextTrainAction.setParams({
                                allBookedOffer: allBookedOffer,
                                nextBookingTicketTypeCode: nextBookingTicketTypeCode,
                                nextTicketStartDate: nextBooking.departureDateTime,
                                nextBooking: nextBooking,
                                trains: allTrips,
                                nextTicketTrainDirection: nextBookingTrip.direction,
                                inboundTrip: inboundTrip,
                                outboundTrip: outboundTrip
                            })
                            await getPreviousAndNextTrainAction.execute()

                            if (isActionRunSuccessfully(getPreviousAndNextTrainAction)) {
                                actionResult.data = getPreviousAndNextTrainAction.result?.data
                            }
                        }
                    }
                }
            }
        } catch (error: any) {
            actionResult.message = "Error fetching get booking for quick exchange:"
            console.error("Error fetching get booking for quick exchange:", error);
        } finally {
            return actionResult;
        }
    }
}

export const getExchangeOfferByBooking = new GetExchangeOfferByBooking()


